import type { Billable, BillablePrice, CustomerDiscount, DiscountItemObject, Product, ProductId } from './api/api'
import { DiscountManager } from './DiscountManager'
import { BillablePriceManager } from './PriceManager'

export class CustomerDiscountData {
  constructor(
    private data: {
      billables: Billable[]
      customerDiscounts: CustomerDiscount[]
      customerBillablePrices: BillablePrice[]
      templateBillablePrices: BillablePrice[]
      currency: string
      enabledBillables: string[]
    },
  ) {}

  private filterEnabledBillables(billable: Billable): boolean {
    return this.data.enabledBillables.includes(billable.billableId)
  }

  private filterIsBillableDiscounted(billable: Billable, discounts: CustomerDiscount[]) {
    return discounts.some((discount) => discount.billableId === billable.billableId)
  }

  private mapBillableToDiscountItemObject(billable: Billable) {
    const discount = this.data.customerDiscounts.find((d) => d.billableId === billable.billableId)

    if (!discount) {
      console.warn(`Could not find discount for billable with id ${billable.billableId}`)
      return undefined
    }

    const priceManager = new BillablePriceManager({
      billablePricesOnCustomer: this.data.customerBillablePrices,
      billablePricesOnTemplate: this.data.templateBillablePrices,
    })

    const prices = priceManager.getAmountWithTypeFromBillable(billable.billableId)

    if (prices.length > 1) {
      console.warn(`Billable with id ${billable.billableId} has more than one price, which is not supported`)
      return undefined
    }

    return new DiscountManager({ discount, price: prices[0], name: billable.name }).getDiscountItemObject()
  }

  filteredDiscountItems(): DiscountItemObject[] {
    return this.data.billables
      .filter((billable) => this.filterIsBillableDiscounted(billable, this.data.customerDiscounts))
      .map<DiscountItemObject | undefined>((billable) => this.mapBillableToDiscountItemObject(billable))
      .filter(Boolean)
  }

  filteredCustomerDiscountProducts() {
    const priceManager = new BillablePriceManager({
      billablePricesOnCustomer: this.data.customerBillablePrices,
      billablePricesOnTemplate: this.data.templateBillablePrices,
    })

    return this.data.billables
      .filter((billable) => this.filterEnabledBillables(billable))
      .filter((billable) => !this.filterIsBillableDiscounted(billable, this.data.customerDiscounts))
      .map<Product>((billable) => ({
        id: billable.billableId as ProductId,
        name: billable.name,
        disabled: priceManager.getAmountWithTypeFromBillable(billable.billableId).length === 0,
      }))
      .filter(Boolean)
  }
}
